import type { KgInsight, KgInsightHeroSection } from '@api/api/kg-insights';
import { KeyStats } from '@design/components/key-stats/KeyStats';
import { SENTIMENT_STYLE_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import { CardPill } from '@terminal/views/insight-feed/components/insight-feed-card/components/card-pill/CardPill';
import { CardType } from '@terminal/views/insight-feed/components/insight-feed-card/InsightFeedCard';
import { EarningsKeyStats } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-key-stats/EarningsKeyStats';
import clsx from 'clsx';
import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { isEarningsCategory } from '../../utils/catalyst-widget-utils';
import { CatalystReaction } from '../catalyst-reaction/CatalystReaction';
import styles from './KgInsightPreview.module.scss';
import { findSection, heroToKeyStats } from './utils/kg-insight-content-utils';

export type KgInsightPreviewProps = {
  kgInsight: KgInsight;
  hideCatalystBadge?: boolean;
  hideChartEntityHeader?: boolean;
  hideMetadataHeader?: boolean;
  keyStatsItemsPerRow?: number;
  children?: React.ReactNode;
};

export const KgInsightPreview = ({
  kgInsight,
  hideCatalystBadge = false,
  hideChartEntityHeader = false,
  hideMetadataHeader = false,
  keyStatsItemsPerRow = 5,
  children,
}: KgInsightPreviewProps) => {
  const { t } = useTranslation(['insights']);

  const heroSection = findSection<KgInsightHeroSection>(
    kgInsight.sections,
    'hero'
  );
  const keyStatsItems = heroSection ? heroToKeyStats(heroSection) : [];

  const publishedDate = new Date(kgInsight.created_at);

  const publishedDateNode = (
    <p className={styles.metadataText}>
      <span>
        {t('insights:catalystGeneration.metadata.publishedWithDate', {
          date: format(publishedDate, 'MMM d, yyyy'),
        })}
      </span>
      <span className={styles.dot} />
      <span>{format(publishedDate, 'h:mm a')}</span>
    </p>
  );

  const isEarnings = isEarningsCategory(kgInsight.category);
  const earningsVariant =
    kgInsight.category === 'earnings_recap'
      ? 'earnings_recap'
      : 'earnings_preview';

  const mapInsightCategoryToCardPillType = {
    company_catalyst: CardType.CompanyCatalyst,
    market_catalyst: CardType.MarketCatalyst,
    earnings_recap: CardType.EarningsRecap,
    earnings_preview: CardType.EarningsPreview,
  };

  return (
    <div className={styles.root} data-testid={`${kgInsight.category}-overview`}>
      {!hideMetadataHeader && (
        <div className={styles.headerWrapper}>
          {hideCatalystBadge && publishedDateNode}
          <div className={styles.insightTypePillWrapper}>
            <div className={styles.insightTypePillGroup}>
              <CardPill
                type={mapInsightCategoryToCardPillType[kgInsight.category]}
              />
              <span className={styles.dot} />
              <span
                className={clsx(
                  styles.sentimentLabel,
                  SENTIMENT_STYLE_MAP[kgInsight.sentiment]
                )}
              >
                {/* i18next-extract-mark-context-next-line["positive", "negative", "neutral"]*/}
                {t('insights:catalystGeneration.metadata.sentiment', {
                  context: kgInsight.sentiment,
                })}
              </span>
            </div>
            {!hideCatalystBadge && publishedDateNode}
          </div>
          <h2 className={styles.title}>{kgInsight.title}</h2>
          <p className={styles.metadataText}>{kgInsight.subtitle}</p>
        </div>
      )}
      {isEarnings ? (
        <EarningsKeyStats
          earningsId={kgInsight.subject.ref?.id}
          variant={earningsVariant}
        />
      ) : (
        keyStatsItems.length > 0 && (
          <KeyStats itemsPerRow={keyStatsItemsPerRow} items={keyStatsItems} />
        )
      )}
      <CatalystReaction
        kgInsight={kgInsight}
        category={kgInsight.category}
        hideChartEntityHeader={hideChartEntityHeader}
      />
      {children}
    </div>
  );
};
